<script setup lang="ts">
import isExternalURL from '~/utils/isExternalURL';
import { NuxtLink } from '#components';
import { EText } from '~/enums/EText';
import TextClip from '~/components/shared/TextClip.vue';

interface Props {
  font?: EText.TFont;
  href?: string | null;
  target?: string;
  size?: EText.TSize;
  sizeMob?: EText.TSize;
  link?: boolean;
  bold?: boolean;
  semiBold?: boolean;
  tag?: string;
  center?: boolean;
  right?: boolean;
  clip?: number;
  block?: boolean;
  grey?: boolean;
  accent1?: boolean;
  white?: boolean;
  secondary?: boolean;
  disabled?: boolean;
  underline?: boolean;
  noUnderline?: boolean;
  through?: boolean;
  noWrap?: boolean;
  preWrap?: boolean;
  animatedUnderline?: boolean;
  html?: string;
  uppercase?: boolean;
  external?: boolean;
  danger?: boolean;
}

const props = withDefaults(defineProps<Props>(), { tag: 'p', target: '_self', size: 'L', font: 'nunito' });
const isTablet = useState<boolean>('isTablet');

const getLinkTag = computed<string | any>(() => {
  if (!props.href) {
    return props.tag;

  } else {
    return isExternalURL(props.href) ? 'a' : NuxtLink;
  }
});

const getSizeModifiers = (): Record<string, boolean> => {
  const modifiers: Record<string, boolean> = {};

  const addModifier = (sizeProp: EText.TSize | undefined, suffix: string = ''): void => {
    const size = sizeProp ? EText.getSize(sizeProp) : undefined;
    if (size) {
      modifiers[`text_${size}${suffix}`] = true;
    }
  };

  if (props.sizeMob && isTablet.value) {
    addModifier(props.sizeMob, '-mobile');
  } else {
    addModifier(props.size);
  }

  return modifiers;
};

const modifiers = computed(() => {
  return {
    ...getSizeModifiers(),
    'text_mobile-modify': props.sizeMob,
    'text_link': props.link,
    'text_bold': props.bold,
    'text_semi-bold': props.semiBold,
    'text_center': props.center,
    'text_right': props.right,
    'text_block': props.block,
    'text_white': props.white,
    'text_secondary': props.secondary,
    'text_grey': props.grey,
    'text_accent1': props.accent1,
    'text_danger': props.danger,
    'text_disabled': props.disabled,
    'text_underline': props.underline,
    'text_noUnderline': props.noUnderline,
    'text_through': props.through,
    'text_href': props.href,
    'text_nowrap': props.noWrap,
    'text_prewrap': props.preWrap,
    'text_animated-underline': props.animatedUnderline,
    'text_uppercase': props.uppercase,
    [`text_${props.font}`]: props.font,
  };
});
</script>

<template>
  <component
    :is="getLinkTag"
    class="text"
    active-class="is-active"
    exact-active-class="is-active-exact"
    :href="getLinkTag === 'a' ? href : undefined"
    :to="getLinkTag === NuxtLink ? href : undefined"
    :target="target"
    :class="modifiers"
    :external="external"
  >
    <TextClip v-if="clip" :max-words="clip">
      <slot>
        <span v-if="html" v-html="html"></span>
      </slot>
    </TextClip>
    <slot v-else>
      <span v-if="html" v-html="html"></span>
    </slot>
  </component>
</template>

<style scoped lang="scss">
.text {
  $block: &;
  color: inherit;
  outline: none;
  width: fit-content;

  &_h1 {
    @include H1();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include H1-mob();
      }
    }
  }
  &_h1Mob {
    @include H1-mob();
  }
  &_h1-mobile {
    @media ($media-tablet) {
      @include H1();
    }
  }
  &_h1Mob-mobile {
    @media ($media-tablet) {
      @include H1-mob();
    }
  }

  &_h2 {
    @include H2();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include H2-mob();
      }
    }
  }
  &_h2Mob {
    @include H2-mob();
  }
  &_h2-mobile {
    @media ($media-tablet) {
      @include H2();
    }
  }
  &_h2Mob-mobile {
    @media ($media-tablet) {
      @include H2-mob();
    }
  }

  &_h3 {
    @include H3();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include H3-mob();
      }
    }
  }
  &_h3Mob {
    @include H3-mob();
  }
  &_h3-mobile {
    @media ($media-tablet) {
      @include H3();
    }
  }
  &_h3Mob-mobile {
    @media ($media-tablet) {
      @include H3-mob();
    }
  }

  &_h4 {
    @include H4();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include H4-mob();
      }
    }
  }
  &_h4Mob {
    @include H4-mob();
  }
  &_h4-mobile {
    @media ($media-tablet) {
      @include H4();
    }
  }
  &_h4Mob-mobile {
    @media ($media-tablet) {
      @include H4-mob();
    }
  }

  &_h5 {
    @include H5();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include H5-mob();
      }
    }
  }
  &_h5Mob {
    @include H5-mob();
  }
  &_h5-mobile {
    @media ($media-tablet) {
      @include H5();
    }
  }
  &_h5Mob-mobile {
    @media ($media-tablet) {
      @include H5-mob();
    }
  }

  &_sub {
    @include Subtitle();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include Subtitle-mob();
      }
    }
  }
  &_subMob {
    @include Subtitle-mob();
  }
  &_sub-mobile {
    @media ($media-tablet) {
      @include Subtitle();
    }
  }
  &_subMob-mobile {
    @media ($media-tablet) {
      @include Subtitle-mob();
    }
  }

  &_l {
    @include TextL();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include TextL-mob();
      }
    }
  }
  &_lMob {
    @include TextL-mob();
  }
  &_l-mobile {
    @media ($media-tablet) {
      @include TextL();
    }
  }
  &_lMob-mobile {
    @media ($media-tablet) {
      @include TextL-mob();
    }
  }

  &_m {
    @include TextM();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include TextM-mob();
      }
    }
  }
  &_mMob {
      @include TextM-mob();
  }
  &_m-mobile {
    @media ($media-tablet) {
      @include TextM();
    }
  }
  &_mMob-mobile {
    @media ($media-tablet) {
      @include TextM-mob();
    }
  }

  &_s {
    @include TextS();

    &:not(#{$block}_mobile-modify) {
      @media ($media-tablet) {
        @include TextS-mob();
      }
    }
  }
  &_sMob {
    @include TextS-mob();
  }
  &_s-mobile {
    @media ($media-tablet) {
      @include TextS();
    }
  }
  &_sMob-mobile {
    @media ($media-tablet) {
      @include TextS-mob();
    }
  }

  &_button {
    @include button();
  }

  &_link {
    @include link();
  }

  &_bold {
    font-weight: 700 !important;
  }

  &_semi-bold {
    font-weight: 500 !important;
  }

  &_center {
    text-align: center;
  }

  &_right {
    text-align: right;
  }

  &_block {
    display: block;
  }

  &_disabled {
    pointer-events: none;
    user-select: none;
  }

  &_grey {
    color: #{$color-neutral-50};
  }

  &_white {
    color: #{$color-neutral-0};
  }

  &_secondary {
    color: #{$color-addcolor-3};
  }

  &_accent1 {
    color: $color-accent-1;
  }

  &_danger {
    color: $color-error;
  }

  &_underline {
    @include underline();
  }

  &_uppercase {
    text-transform: uppercase !important;
  }

  &_through {
    text-decoration: line-through;
  }

  &_href:not(#{$block}_noUnderline) {
    @include link();
  }

  &:not(#{$block}_noUnderline)::v-deep(a) {
    @include underline();
  }

  :deep(b) {
    font-weight: 500;
  }

  :deep(.underline) {
    @include underline();
  }

  &_tenor {
    font-family: $font-tenor;
  }

  &_nunito {
    font-family: $font-nunito;
  }

  &_nowrap {
    white-space: nowrap;
  }

  &_prewrap {
    white-space: pre-wrap;
  }

  &_animated-underline {
    position: relative;

    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 0;
      height: 1px;
      background-color: currentColor;
      transition: none; /* Отключаем обычный transition */
    }

    &:hover:after {
      animation: expand 0.3s forwards;
    }

    &:not(:hover):after {
      animation: collapse 0.3s forwards;
    }
  }

  @keyframes expand {
    from {
      width: 0;
      left: 0;
    }
    to {
      width: 100%;
      left: 0;
    }
  }

  @keyframes collapse {
    from {
      width: 100%;
      left: 0;
    }
    to {
      width: 0;
      left: 100%;
    }
  }
}
</style>
